import React from "react";
import { makeStyles } from "@material-ui/core";
import { RB_PAY_SETUP_COLORS, RB_PAY_SETUP_COMPLETE_NOTE } from "../constants/rbPaySettings.constants";
import { RBPaySetupTheme } from "../utils/rbPaySettings.theme";

const C = RB_PAY_SETUP_COLORS;

const useStyles = makeStyles({
  card: {
    background: C.cardBg,
    border: `1px solid ${C.cardBorder}`,
    borderRadius: 12,
    padding: "20px 28px 18px",
    marginBottom: 18,
    boxShadow: "0 1px 4px rgba(26,16,53,0.05)",
  },
  track: {
    display: "flex",
    alignItems: "flex-start",
    marginBottom: 16,
  },
  step: {
    flexShrink: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "5px",
    width: 120,
  },
  circle: {
    width: 34,
    height: 34,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 13,
    fontWeight: 700,
  },
  label: {
    fontSize: 12.5,
    textAlign: "center",
    margin: 0,
    lineHeight: 1.3,
  },
  caption: {
    fontSize: 11,
    textAlign: "center",
    margin: 0,
    lineHeight: 1.3,
    color: C.textFaint,
  },
  connector: {
    flex: 1,
    height: 2,
    marginTop: 16,
    borderRadius: 1,
  },
  notice: {
    display: "flex",
    alignItems: "flex-start",
    gap: "8px",
    borderRadius: 8,
    padding: "10px 14px",
  },
  noticeText: {
    fontSize: 12,
    margin: 0,
    lineHeight: 1.5,
  },
});

interface StepItem {
  // Generic string key so both Worker (profile/payment/authorization) and
  // Owner (banking/authorization) flows can reuse this tracker.
  key: string;
  label: string;
  caption: string;
  complete: boolean;
}

// A state-aware checklist notice. `tone` picks the success (green) vs warn
// (amber) styling; `message` is the copy. When omitted the component falls back
// to the legacy isSetupComplete-driven success/warn message.
export interface RBPayStepNotice {
  tone: "success" | "warn";
  message: string;
}

interface RBPayStepProgressProps {
  steps: StepItem[];
  activeStepKey?: string;
  isSetupComplete: boolean;
  notice?: RBPayStepNotice;
  theme: RBPaySetupTheme;
  onStepClick?: (key: string) => void;
}

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path
      d="M2.5 7l3.5 3.5 5.5-6"
      stroke="white"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const RBPayStepProgress: React.FC<RBPayStepProgressProps> = ({
  steps,
  activeStepKey,
  isSetupComplete,
  notice,
  theme,
  onStepClick,
}) => {
  const classes = useStyles();
  const activeIndex = steps.findIndex((step) => step.key === activeStepKey);

  // Fall back to the legacy isSetupComplete-driven notice when a caller hasn't
  // supplied an explicit, state-aware one.
  const resolvedNotice: RBPayStepNotice =
    notice ||
    (isSetupComplete
      ? { tone: "success", message: RB_PAY_SETUP_COMPLETE_NOTE }
      : {
          tone: "warn",
          message: "Your setup is not complete until you click Save Setup at the bottom of this page.",
        });
  const isSuccessNotice = resolvedNotice.tone === "success";

  return (
    <div className={classes.card}>
      <div className={classes.track}>
        {steps.map((step, index) => {
          const isDone = step.complete;
          const isCurrent = !isDone && index === activeIndex;
          const circleBg = isDone || isCurrent ? theme.primary : C.stepInactiveBg;
          const circleColor = isDone || isCurrent ? "#fff" : C.stepInactiveText;
          const labelColor = isDone || isCurrent ? theme.primary : "#6B5F8A";
          const labelWeight = isDone || isCurrent ? 600 : 500;
          const connectorActive = steps[index]?.complete || index < activeIndex;

          return (
            <React.Fragment key={step.key}>
              <div
                className={classes.step}
                style={onStepClick ? { cursor: "pointer" } : undefined}
                onClick={onStepClick ? () => onStepClick(step.key) : undefined}
                role={onStepClick ? "button" : undefined}
              >
                <div className={classes.circle} style={{ background: circleBg, color: circleColor }}>
                  {isDone ? <CheckIcon /> : index + 1}
                </div>
                <p className={classes.label} style={{ color: labelColor, fontWeight: labelWeight }}>
                  {step.label}
                </p>
                <p className={classes.caption}>{step.caption}</p>
              </div>
              {index < steps.length - 1 ? (
                <div
                  className={classes.connector}
                  style={{ background: connectorActive ? theme.primary : C.stepInactiveBg }}
                />
              ) : null}
            </React.Fragment>
          );
        })}
      </div>

      {isSuccessNotice ? (
        <div
          className={classes.notice}
          style={{ background: C.successBg, border: `1px solid ${C.successBorder}` }}
        >
          <svg width="15" height="15" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, marginTop: 1 }}>
            <circle cx="8" cy="8" r="7" fill={C.successIcon} />
            <path d="M5 8l2.5 2.5 4-4.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <p className={classes.noticeText} style={{ color: C.successText }}>
            {resolvedNotice.message}
          </p>
        </div>
      ) : (
        <div
          className={classes.notice}
          style={{ background: C.warnBg, border: `1px solid ${C.warnBorder}` }}
        >
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, marginTop: 1 }}>
            <circle cx="8" cy="8" r="6.5" stroke={C.warnIcon} strokeWidth="1.4" />
            <line x1="8" y1="4.5" x2="8" y2="9" stroke={C.warnIcon} strokeWidth="1.4" strokeLinecap="round" />
            <circle cx="8" cy="11.2" r="0.8" fill={C.warnIcon} />
          </svg>
          <p className={classes.noticeText} style={{ color: C.warnText }}>
            {resolvedNotice.message}
          </p>
        </div>
      )}
    </div>
  );
};
